<template>
  <!-- XOL-602 (UX spec sections 16 and 17, R2.3.2): the page an "invite by e-mail" link
       opens. Route /register/:token, rendered by Login.vue in mode `invite`. The invitee
       chooses their own Login ID and password, and the API creates an enabled contact in
       the invited Tower Co org. Signed-out browsers only (D11): a signed-in browser is sent
       to the app by lib/authGuard.js and this page never renders, so no signed-in variant.

       THE TOKEN goes in a POST body field named `token`, never in a URL, Vuex or storage.
       THE E-MAIL IS SHOWN, NEVER SENT BACK: the API uses the invitation's own address. -->
  <login-panel>

    <template v-if="state === 'checking'">
      <p role="status" class="login-panel__loading"><i class="fa fa-spinner fa-spin text-primary" aria-hidden="true"></i> Checking your invitation…</p>
    </template>

    <template v-else-if="state === 'inspect_error'">
      <el-alert v-if="inspectRate" type="warning" show-icon :closable="false"
        title="Too many attempts." description="Wait one minute, then try again." />
      <el-alert v-else type="error" show-icon :closable="false" title="Couldn't check this invitation." />
      <div class="login-panel__actions">
        <div><el-button size="small" @click="inspect">Try again</el-button></div>
        <div><el-button type="text" icon="fa fa-chevron-left" @click="act('back')">Back to login</el-button></div>
      </div>
    </template>

    <!-- 17. Invite link states. -->
    <template v-else-if="state === 'problem'">
      <el-alert :type="problem.type" show-icon :closable="false" :title="problem.title" :description="problem.desc" />
      <div class="login-panel__actions">
        <div v-for="(b, i) in problem.buttons" :key="b.key">
          <el-button v-if="i === 0" ref="firstAction" type="primary" :icon="b.icon" @click="act(b.key)">{{ b.label }}</el-button>
          <el-button v-else type="text" @click="act(b.key)">{{ b.label }}</el-button>
        </div>
      </div>
    </template>

    <!-- 16. The form. -->
    <template v-else>
      <h4 ref="heading" class="login-panel__title" tabindex="-1">Create your account</h4>
      <p class="login-panel__text">{{ intro }}</p>

      <el-form label-position="top" @submit.native.prevent>
        <el-form-item label="E-mail"><b>{{ email }}</b></el-form-item>
        <el-form-item label="Organisation"><b>{{ organisation }}</b></el-form-item>

        <el-form-item label="Full Name" for="invite-reg-name" required :error="nameError">
          <el-input id="invite-reg-name" ref="name" v-model="v.name" maxlength="120" autocomplete="name"
            :disabled="saving" @blur="touched.name = true" @keyup.native.enter="onEnter" />
        </el-form-item>

        <el-form-item label="Login ID" for="invite-reg-login" required :error="loginError">
          <el-input id="invite-reg-login" ref="login" v-model="v.login" maxlength="80" autocomplete="username"
            :disabled="saving" @blur="touched.login = true" @keyup.native.enter="onEnter" />
          <small class="login-panel__meta" style="margin: 6px 0 0; line-height: 1.4;">You will use this to log in.</small>
        </el-form-item>

        <el-form-item label="Phone" for="invite-reg-phone" :error="errors.phone">
          <el-input id="invite-reg-phone" ref="phone" v-model="v.phone" type="tel" maxlength="45" autocomplete="tel"
            :disabled="saving" @keyup.native.enter="onEnter" />
        </el-form-item>

        <el-form-item label="Job Title" for="invite-reg-job" :error="errors.jobTitle">
          <el-input id="invite-reg-job" ref="jobTitle" v-model="v.jobTitle" maxlength="80" autocomplete="organization-title"
            :disabled="saving" @keyup.native.enter="onEnter" />
        </el-form-item>
      </el-form>

      <div @keyup.enter="onEnter">
        <password-form v-model="form" :inline="false" :password-error="errors.password"
          @confirm-blur="confirmTouched = true" />
      </div>

      <small v-if="expiresText" class="login-panel__meta">This link expires on {{ expiresText }}.</small>

      <el-alert v-if="alertKey" id="password-rules" type="error" show-icon :closable="false" :title="alertText" />
      <el-alert v-if="alert === 'rate'" type="warning" show-icon :closable="false"
        title="Too many attempts." description="Wait one minute, then try again." />
      <el-alert v-if="alert === 'error'" type="error" show-icon :closable="false"
        title="Couldn't create your account. Try again." />

      <div class="login-panel__actions">
        <div>
          <el-button type="success" icon="fa fa-check" :loading="saving"
            :disabled="!canSave" @click="submit">{{ saving ? 'Creating…' : 'Create account' }}</el-button>
        </div>
        <div><el-button type="text" icon="fa fa-chevron-left" @click="act('back')">Back to login</el-button></div>
      </div>
    </template>

  </login-panel>
</template>

<script>

import axios from '@/lib/axiosClient'
import DateMixins from '@/mixins/DateMixins'
import LoginPanel from '@/components/login/LoginPanel'
import PasswordForm from '@/components/login/PasswordForm'
import { passwordProblem, passwordProblemText, visiblePasswordProblem } from '@/lib/passwordPolicy'
import { errStatus, linkCode, fieldMessage, wasRetried } from '@/lib/linkErrors'

// The name rule the API enforces (ContactRules::NAME_MIN).
var NAME_MIN = 3

export default {

  name: 'invite-register',

  mixins: [DateMixins],

  components: {
    LoginPanel,
    PasswordForm
  },

  props: {
    // The invitation's secret, from the route. Only ever sent in a request body.
    token: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      // 'checking' | 'form' | 'problem' | 'inspect_error'
      state: 'checking',
      // 'expired' | 'used' | 'used_retry' | 'invalid' | 'email_in_use'
      problemCode: 'invalid',
      inspectRate: false,

      // From the inspect response.
      email: '',
      organisation: '',
      // The name the inviter gave (it pre-fills Full Name, which stays editable).
      inviteName: '',
      expiresAt: null,

      v: { name: '', login: '', phone: '', jobTitle: '' },
      touched: { name: false, login: false },
      // The API's 422 messages, per field. A message belongs to the value that was sent:
      // editing that field clears it.
      errors: { name: '', login: '', phone: '', jobTitle: '', password: '' },

      form: { password: null, c_password: null },
      confirmTouched: false,
      saving: false,
      // null | 'rate' | 'error'
      alert: null,
      // A submit ended without a definite answer: the account may have been created.
      submitUncertain: false
    }
  },

  computed: {
    cleanName () {
      return (this.v.name || '').trim()
    },

    cleanLogin () {
      return (this.v.login || '').trim()
    },

    intro () {
      var hello = this.inviteName ? 'Hello ' + this.inviteName + '.' : 'Hello.'
      return hello + ' You are invited to join ' + this.organisation + ' on the Tower Management System. Fill in your details to create your account.'
    },

    expiresText () {
      return this.expiresAt ? this.formatDateTime(this.expiresAt) : ''
    },

    nameError () {
      if (this.errors.name) {
        return this.errors.name
      }
      if (!this.touched.name) {
        return ''
      }
      if (!this.cleanName) {
        return 'Enter your full name.'
      }
      return this.cleanName.length < NAME_MIN ? 'Use at least 3 characters.' : ''
    },

    loginError () {
      if (this.errors.login) {
        return this.errors.login
      }
      return (this.touched.login && !this.cleanLogin) ? 'Choose a Login ID.' : ''
    },

    // The rule runs against the Login ID the invitee CHOSE, and re-runs when it changes.
    problemKey () {
      return passwordProblem(this.form.password, this.form.c_password, this.cleanLogin)
    },

    alertKey () {
      return visiblePasswordProblem(this.form.password, this.form.c_password, this.cleanLogin, this.confirmTouched)
    },

    alertText () {
      return passwordProblemText(this.alertKey)
    },

    canSave () {
      return !this.saving &&
        this.cleanName.length >= NAME_MIN &&
        !!this.cleanLogin &&
        this.problemKey === null
    },

    // The 410 states of UX section 17.
    problem () {
      var c = this.problemCode
      var type, title, desc

      if (c === 'expired') {
        type = 'warning'
        title = 'This invitation has expired'
        desc = 'Invitations expire after 3 days. Ask your administrator to send you a new one.'
      } else if (c === 'used') {
        type = 'info'
        title = 'This invitation has already been used'
        desc = 'If you created your account with it, log in with your Login ID. If not, ask your administrator for a new invitation.'
      } else if (c === 'used_retry') {
        type = 'warning'
        title = 'Your account may already be created'
        desc = 'We could not confirm it, but this invitation has now been used. Try to log in with the Login ID and password you chose. If that does not work, ask your administrator for help.'
      } else if (c === 'email_in_use') {
        type = 'info'
        title = 'An account already uses this e-mail address'
        desc = 'You may already have an account. Log in, or use Forgot password? to choose a new password. If you need help, ask your administrator.'
      } else {
        type = 'error'
        title = 'This invitation link is not valid'
        desc = 'The link may be cut off. Open the newest invitation e-mail, or ask your administrator for a new one.'
      }

      var buttons = [{ key: 'back', label: 'Back to login', icon: 'fa fa-right-to-bracket' }]
      if (c === 'email_in_use') {
        buttons.push({ key: 'forgot', label: 'Forgot password?', icon: '' })
      }

      return { type: type, title: title, desc: desc, buttons: buttons }
    }
  },

  watch: {
    'v.name': function () { this.errors.name = '' },
    'v.login': function () { this.errors.login = '' },
    'v.phone': function () { this.errors.phone = '' },
    'v.jobTitle': function () { this.errors.jobTitle = '' },
    'form.password': function () { this.errors.password = '' },
    'form.c_password': function () { this.errors.password = '' }
  },

  methods: {

    // POST /registration/invite {token}: shows the invitation. Never uses it up.
    inspect () {
      this.state = 'checking'
      this.inspectRate = false

      axios.post(this.getEndpoint('registration/invite', null, false), { token: this.token })
        .then(response => {
          var data = (response && response.data) || {}
          this.email = data.email || ''
          this.organisation = (data.organisation && data.organisation.name) || ''
          this.inviteName = data.name || ''
          this.v.name = data.name || ''
          this.expiresAt = data.expires_at || null
          this.state = 'form'
          this.focusHeading()
        }, err => {
          var status = errStatus(err)

          // A 410 says something about the INVITATION, not the session: nothing here touches auth.
          if (status === 410) {
            this.showProblem(linkCode(err))
          } else if (status === 422) {
            this.showProblem('invalid')
          } else {
            this.inspectRate = status === 429
            this.state = 'inspect_error'
          }
        })
    },

    // POST /registration/invite/accept: creates the account and uses the invitation up.
    submit () {
      if (!this.canSave) {
        return
      }

      this.saving = true
      this.alert = null
      this.clearErrors()

      // The EXACT body: the token and what the invitee typed. Never `email` (the API uses the
      // invitation's own address and ignores one sent), and optional fields only when filled.
      var body = {
        token: this.token,
        name: this.cleanName,
        login: this.cleanLogin,
        password: this.form.password.trim()
      }
      var phone = (this.v.phone || '').trim()
      var jobTitle = (this.v.jobTitle || '').trim()
      if (phone) {
        body.phone = phone
      }
      if (jobTitle) {
        body.jobTitle = jobTitle
      }

      axios.post(this.getEndpoint('registration/invite/accept', null, false), body)
        .then(() => {
          this.saving = false
          var login = this.cleanLogin
          this.form.password = null
          this.form.c_password = null
          // Success lives in memory on the Login screen, and the token URL leaves the
          // current history entry.
          this.$emit('done', { variant: 'registered', login: login })
          this.$router.replace('/password-set')
        }, err => {
          this.saving = false
          var status = errStatus(err)

          if (status === 422) {
            this.showFieldErrors(err)
          } else if (status === 410) {
            var code = linkCode(err)
            // `used` right after a submit that may have succeeded (timed out, or replayed
            // by axios-retry): the invitation was probably used by THIS submit.
            if (code === 'used' && (this.submitUncertain || wasRetried(err))) {
              code = 'used_retry'
            }
            this.showProblem(code)
          } else if (status === 429) {
            this.alert = 'rate'
          } else {
            this.submitUncertain = true
            this.alert = 'error'
          }
        })
    },

    clearErrors () {
      this.errors = { name: '', login: '', phone: '', jobTitle: '', password: '' }
    },

    // Each 422 message goes on its field, and focus moves to the first one. The invitation is
    // NOT used up by a 422. A 422 about nothing we show (the token) reads as "not valid".
    showFieldErrors (err) {
      var order = ['name', 'login', 'phone', 'jobTitle', 'password']
      var first = null
      var errors = { name: '', login: '', phone: '', jobTitle: '', password: '' }

      order.forEach(field => {
        var message = fieldMessage(err, field)
        if (message) {
          errors[field] = message
          first = first || field
        }
      })

      if (!first) {
        this.showProblem('invalid')
        return
      }

      this.errors = errors
      this.focusField(first)
    },

    showProblem (code) {
      this.problemCode = code
      this.state = 'problem'
      // The XOL-647 terminal-state rule: focus goes to the first button.
      this.$nextTick(() => {
        var first = this.$refs.firstAction
        first = Array.isArray(first) ? first[0] : first
        if (first && first.$el && typeof first.$el.focus === 'function') {
          first.$el.focus()
        }
      })
    },

    onEnter (event) {
      if (event && event.target && event.target.tagName === 'INPUT') {
        this.submit()
      }
    },

    act (key) {
      if (key === 'forgot') {
        this.$router.push('/forgot-password')
        return
      }
      // After a submit that may have created the account, "Back to login" arrives with the
      // Login ID they chose already typed.
      if (this.problemCode === 'used_retry' && this.cleanLogin) {
        this.$emit('prefill-login', this.cleanLogin)
      }
      this.$router.push('/login')
    },

    focusHeading () {
      this.$nextTick(() => {
        var heading = this.$refs.heading
        if (heading && typeof heading.focus === 'function') {
          heading.focus()
        }
      })
    },

    focusField (field) {
      this.$nextTick(() => {
        if (field === 'password') {
          var el = this.$el && this.$el.querySelector ? this.$el.querySelector('.Password__field') : null
          if (el) {
            el.focus()
          }
          return
        }
        var input = this.$refs[field]
        if (input && typeof input.focus === 'function') {
          input.focus()
        }
      })
    }

  },

  created () {
    this.inspect()
  }

}

</script>
